import {
  RiskLocationPreview,
  RiskPreviewAdmissionRecord,
  RiskPreviewAppointment,
  RiskPreviewHospital,
  RiskPreviewPatient,
  RiskPreviewProvisionalReason,
  previewRiskLocation,
} from '../../../core/utils/risk-location-preview';

/**
 * Shared vocabulary for the risk-management suggestion surfaces — workstreams E and F of
 * `tasks/risk-management.md`.
 *
 * Two components render AI-suggested facility issues (the AI request detail, E2; the ward-round
 * note detail, F2) and both open the same confirm-before-add dialog (E3/F3). Everything they must
 * agree on lives here as pure functions, so a label or a taxonomy that changes changes once.
 */

/** D7 — the CLOSED category taxonomy. Free text was rejected deliberately; see D7. */
export const RISK_CATEGORIES = ['consumables', 'equipment', 'medication', 'staffing', 'facility', 'process'] as const;
export type RiskCategory = typeof RISK_CATEGORIES[number];

export const RISK_SEVERITIES = ['low', 'medium', 'high'] as const;
export type RiskSeverity = typeof RISK_SEVERITIES[number];

/**
 * D9 — the status lifecycle, in DISPLAY ORDER (which is also lifecycle order).
 *
 * Hoisted here because it had quietly escaped the rule the two taxonomies above exist to enforce:
 * it was restated verbatim in five places — three `statusOptions` arrays, one `OPEN_RISK_STATUSES`
 * and the server's own `STATUSES` — and a closed vocabulary written out five times is a closed
 * vocabulary waiting to disagree with itself.
 *
 * SERVER-SIDE COUNTERPART: `STATUSES` in `controllers/patientRiskItemController.js`, which
 * validates the same four values on write. It cannot import this file, so the two are kept in step
 * by hand and each names the other.
 *
 * NO `riskStatusLabel` HELPER, deliberately. `riskCategoryLabel` / `riskSeverityLabel` exist
 * because those values are stored lowercase and need casing for display; these are stored in their
 * display form and every current call site renders them raw. A helper that only ever returned its
 * argument would be a place for the two forms to drift apart later.
 */
export const RISK_STATUSES = ['Open', 'Reported', 'Resolved', 'Dismissed'] as const;
export type RiskStatus = typeof RISK_STATUSES[number];

/**
 * The statuses that still need somebody's attention — what an "outstanding issues" badge counts.
 *
 * `Reported` IS OUTSTANDING, and that is the whole reason this constant is worth naming rather
 * than being re-derived per call site. It means the issue was put in front of the facility and
 * nobody has come back on it yet — the matter is still open, it is merely open with someone else.
 * A badge that counted only `Open` would drop to zero the instant a register was exported, which
 * reads as "the register emptied" at exactly the moment the practice is owed a response. Only
 * `Resolved` and `Dismissed` are genuinely finished, and they are finished for opposite reasons.
 */
export const OPEN_RISK_STATUSES = ['Open', 'Reported'] as const;

/** One entry of `AIRequest.suggestedRiskItems.items` (§5a). Every field is optional on purpose. */
export interface RiskSuggestion {
  issue?: string;
  category?: string;
  severity?: string;
  /** D8 — the VERBATIM sentence. Evidence, never edited, and shown before anything is confirmed. */
  quote?: string;
  score?: number;
  reason?: string;
}

/**
 * `AIRequest.suggestedRiskItems` in every shape it actually arrives in, collapsed to an array.
 *
 * THE COLUMN IS NULL, NOT `[]`, FOR MOST ROWS. It is nullable and stays NULL for every request
 * that predates the feature and every recording whose extraction found nothing — so `null` is the
 * NORMAL case, not an error, and `.items` must never be dereferenced blind. The other shapes are
 * real too: `ai-dialog.component.ts` already flattens the column to a bare array before putting it
 * on an editing note, so the note-detail side (F1) receives `RiskSuggestion[]` while the AI
 * request side (E2) receives `{ items: [...] }` straight off the model.
 *
 * Anything malformed collapses to `[]` rather than being half-parsed. These suggestions end up on
 * a doctor's letterhead (R4); "nothing found" is a safe answer and a partially-read blob is not.
 */
export function normaliseRiskSuggestions(raw: any): RiskSuggestion[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(item => !!item && typeof item === 'object');
  if (Array.isArray(raw.items)) return raw.items.filter((item: any) => !!item && typeof item === 'object');
  return [];
}

/** Display label for a D7 category. An off-taxonomy value from the model is shown as-is. */
export function riskCategoryLabel(category?: string): string {
  const map: { [key: string]: string } = {
    consumables: 'Consumables',
    equipment: 'Equipment',
    medication: 'Medication',
    staffing: 'Staffing',
    facility: 'Facility',
    process: 'Process',
  };
  if (!category) return 'Uncategorised';
  return map[String(category).toLowerCase()] || category;
}

export function riskSeverityLabel(severity?: string): string {
  if (!severity) return 'Unrated';
  const value = String(severity).toLowerCase();
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * The §4 branch that produced a facility, in words a doctor can act on.
 *
 * D6 exists so that "this happened in my rooms" and "we could not work out where this happened"
 * are never the same row on a document handed to a hospital. `'rooms'` / `'none'` is therefore
 * labelled as the FIRST-CLASS OUTCOME it is, never as a failure.
 */
export function riskResolutionLabel(resolvedBy?: string | null, location?: string | null): string {
  switch (resolvedBy) {
    // NOT a §4 branch, and deliberately worded so it cannot be mistaken for one. The doctor
    // overrode the resolution chain in the add dialog and the server VALIDATED and honoured that
    // choice. D6's purpose is that a location always says how it was arrived at, so "the chain
    // worked this out" and "a human asserted this" must never render as the same sentence.
    case 'manual':
      return 'Chosen by you on the add form';
    case 'inpatient-status':
      return 'Inpatient — from the patient\'s current admission';
    case 'appointment':
      return 'Procedure — from the booked appointment for this encounter';
    case 'admission-history':
      return 'Inpatient — from the admission history covering this date';
    case 'none':
      return 'In-rooms / OPD — no hospital admission covers this date';
    default:
      break;
  }
  // No `resolvedBy` (an item read back from the register, where it is not a column) — fall back
  // to the persisted discriminator.
  switch (location) {
    case 'inpatient': return 'Inpatient';
    case 'procedure': return 'Procedure';
    case 'rooms': return 'In-rooms / OPD';
    default: return 'Not recorded';
  }
}

/** Short badge for the `location` discriminator (D6). */
export function riskLocationLabel(location?: string | null): string {
  switch (location) {
    case 'inpatient': return 'Inpatient';
    case 'procedure': return 'Procedure';
    case 'rooms': return 'In-rooms / OPD';
    default: return 'Unknown';
  }
}

/** What the client can work out about the facility before the server answers. */
export interface RiskFacilityPrediction {
  hospitalId: string | null;
  hospitalName: string | null;
  wardName: string | null;
  location: string;
  resolvedBy: string;
  /**
   * True when the client could NOT reach a confident answer and the server may still resolve the
   * facility from admission history (§4 branch 3, which needs a `PatientAdmissionHistory` query
   * and a name→`Hospitals` match the client cannot do). The dialog says so out loud rather than
   * asserting "in-rooms" and being contradicted a second later.
   */
  provisional: boolean;
  /**
   * WHICH uncertainty applies, so the dialog can say something a doctor can act on rather than a
   * generic "this might change". One of the four cases enumerated in `risk-location-preview.ts`:
   * stale admission records, a name matching no ACTIVE hospital, an appointment the browser does
   * not hold, or two same-day admissions naming different hospitals.
   */
  provisionalReason?: RiskPreviewProvisionalReason | null;
}

/**
 * THE FACILITY THE DOCTOR CHOSE — the four snapshot columns, decided client-side and stored as
 * given by `addRiskItem`.
 *
 * ── THIS REVERSES D1-1, DELIBERATELY ────────────────────────────────────────────────────
 *
 * The original rule was "never trust the client's facility": `hospitalId` / `hospitalName` /
 * `wardName` / `location` are what a hospital reads off the exported PDF, so the server resolved
 * them from §4 and discarded whatever the client said. That has been reversed on the user's
 * instruction, and the reasoning is worth keeping: this is the practice's OWN governance record,
 * the doctor was standing in the building, and §4 is an inference from admission and appointment
 * data that is sometimes simply wrong. A resolver overruling the person who was there is the worse
 * failure of the two.
 *
 * So the picker in the add dialog is pre-filled from the §4 preview and the doctor may change it,
 * and the server performs NO ownership or ward-membership check and has no rejection path. §4
 * survives on the server for one job only: supplying the facility when a caller sends no choice at
 * all.
 *
 * Because the server stores what it is told, IT CAN NEVER DISAGREE — which is what retired the
 * post-save reconciliation toast and the preview's four "provisional" warnings. The preview is no
 * longer a prediction that might be overruled; it is just the default in a form.
 *
 * The shape is the four columns verbatim rather than a mode/id pair, so that what the dialog emits,
 * what the POST carries and what is stored are one thing and not three translations of it.
 */
export interface RiskFacilityChoice {
  hospitalId: string | null;
  /** The SNAPSHOT (D5) — kept as text so the row survives the hospital being renamed later. */
  hospitalName: string | null;
  /** Snapshot too, and optional: a theatre case is booked to a hospital, not to a bed. */
  wardName: string | null;
  /** D6's discriminator. `'rooms'` means all three fields above are null, and is a real answer. */
  location: 'inpatient' | 'procedure' | 'rooms';
}

/**
 * A CLIENT-SIDE PREVIEW of §4, for the confirm-before-add dialog (E3/R4).
 *
 * THIS IS NOT THE AUTHORITY AND MUST NEVER BE SENT TO THE SERVER. `utils/riskHospitalResolver.js`
 * resolves the facility server-side at create time and the controller discards whatever the client
 * says about it — deliberately, because these four columns are what a hospital reads off the
 * exported PDF. This function exists only so the doctor sees the likely answer BEFORE committing,
 * which is one of R4's three controls.
 *
 * ── E3-1: WHY THIS IS NOW A ONE-LINE DELEGATION ─────────────────────────────────────────
 *
 * It used to carry its own copy of branches 1, 2 and 4, and assumed branch 3 was impossible in the
 * browser. Both of those are now fixed in one move: `core/utils/risk-location-preview.ts` runs ALL
 * FOUR branches — `hospitals$` carries wards nested and `patientAdmissionRecords$` is already
 * loaded in the patient file, which is where both callers live — and it lives in `core/utils`
 * because a rule set that must not drift from the server needs to be a pure module a spec and a
 * parity harness can drive directly, not a helper buried in a component folder.
 *
 * The body is gone rather than left in place because a SECOND copy of the §4 rules is the drift
 * this whole design is trying to prevent. There is now exactly one client implementation, and it
 * is the one under test.
 */
export function predictRiskFacility(args: {
  patient?: RiskPreviewPatient | null;
  hospitals?: RiskPreviewHospital[] | null;
  appointments?: RiskPreviewAppointment[] | null;
  /** `patientAdmissionRecords$` for this patient — what makes §4 branch 3 possible client-side. */
  admissionRecords?: RiskPreviewAdmissionRecord[] | null;
  /**
   * The encounter id THE CALLER IS GOING TO POST — normally `pickEncounterAppointment(...)?.id`.
   * The server runs branch 2 off the `appointmentId` in the body and nothing else, so previewing
   * any other appointment would be previewing a branch the server is not going to run.
   */
  appointmentId?: string | null;
  dateOfIssue?: any;
  /** Tenancy for the branch-3 name match (D5). Defaults to the patient's own `groupId`. */
  groupId?: string | null;
}): RiskFacilityPrediction {
  const preview: RiskLocationPreview = previewRiskLocation(args);
  return preview;
}

// `riskProvisionalNote` USED TO LIVE HERE and has been removed along with the rest of the
// provisional messaging. It existed because the client's preview was a PREDICTION the server could
// overrule, so the dialog had to name which of the four uncertainties applied. The client now
// decides the facility outright and the server stores it, so there is no longer anything for a
// prediction to be wrong about. `previewRiskLocation` still returns `provisional` /
// `provisionalReason` — that module is unchanged and out of scope — and nothing renders them.

/**
 * The appointment that names this encounter, for §4 branch 2 — and for the `appointmentId` the
 * create call passes so the server can run that branch at all.
 *
 * NARROW ON PURPOSE: the same patient, the same calendar day, and a `hospitalId` actually set.
 * `models/patient-appointment.js` sets `hospitalId` only for PROCEDURE appointments (a
 * consultation legitimately leaves it NULL), so anything looser would hand the resolver an
 * appointment that answers nothing, and a wrong one would snapshot a facility that was never
 * involved onto a letterheaded complaint.
 */
export function pickEncounterAppointment(appointments: any[] | null | undefined, dateOfIssue: any, patient?: any): any | null {
  if (!Array.isArray(appointments) || !appointments.length || !dateOfIssue) return null;

  const day = toCalendarDay(dateOfIssue);
  if (!day) return null;

  const candidates = appointments.filter((appointment: any) => {
    if (!appointment || !appointment.hospitalId) return false;
    if (patient && patient.id && appointment.patientId && appointment.patientId !== patient.id) return false;
    return toCalendarDay(appointment.date ?? appointment.startDate ?? appointment.appointmentDate) === day;
  });

  // Exactly one is an answer; two are an ambiguity, and guessing between two hospitals is the one
  // thing §4 refuses to do. Ambiguity falls through to the next branch.
  return candidates.length === 1 ? candidates[0] : null;
}

/**
 * 'YYYY-MM-DD' for any date-ish value, or null. No format guessing — these are real dates, not
 * `PatientAdmissionHistory`'s mixed strings.
 *
 * `toDate` is checked explicitly because a `moment` reaches here from the dialog's datepicker.
 * `new Date(moment)` happens to work through `valueOf()`, but relying on that coercion is how a
 * date silently becomes `Invalid Date` the day someone swaps the date library.
 */
function toCalendarDay(value: any): string | null {
  if (!value) return null;
  const date = value instanceof Date
    ? value
    : (typeof value.toDate === 'function' ? value.toDate() : new Date(value));
  if (!(date instanceof Date)) return null;
  if (isNaN(date.getTime())) return null;
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}
